import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { Sync } from '@mui/icons-material'
import { brandColors, serif } from './brand'
import { SyncRun, ago, getSyncStatus, runSync, usePolled } from './api'
import { Page } from './ui'

function since(iso: string): string {
  const a = ago(iso)
  return a === 'just now' ? a : `${a} ago`
}

function RunChip({ run }: { run?: SyncRun }): React.JSX.Element {
  if (!run) return <Chip size='small' variant='outlined' label='Never synced' />
  if (!run.finishedAt) return <Chip size='small' label='Syncing…' />
  return (
    <Chip
      size='small'
      color={run.ok ? 'success' : 'error'}
      variant='outlined'
      label={`${run.ok ? 'Synced' : 'Sync failed'} ${since(run.finishedAt)}`}
    />
  )
}

function LogDialog(props: {
  run?: SyncRun
  onClose: () => void
}): React.JSX.Element {
  const c = brandColors[useTheme().palette.mode]
  return (
    <Dialog
      open={Boolean(props.run)}
      onClose={props.onClose}
      maxWidth='md'
      fullWidth
    >
      <DialogTitle className='erecords-serif' sx={{ fontFamily: serif }}>
        Entra sync #{props.run?.id} ({props.run?.trigger})
      </DialogTitle>
      <DialogContent>
        <Box
          component='pre'
          sx={{
            m: 0,
            p: 1.5,
            borderRadius: '10px',
            bgcolor: c.bg,
            fontSize: 12,
            whiteSpace: 'pre-wrap',
            maxHeight: '60vh',
            overflow: 'auto',
          }}
        >
          {props.run?.log || '(no output)'}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

// EntraSyncPanel shows the Microsoft Entra sync status with a "Sync now"
// button (admins). compact renders a one-line header widget; otherwise a card
// with the recent runs (Admin > Entra sync).
export default function EntraSyncPanel(props: {
  compact?: boolean
}): React.JSX.Element {
  const c = brandColors[useTheme().palette.mode]
  const status = usePolled(getSyncStatus, 60_000)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [shown, setShown] = useState<SyncRun>()

  const sync = (): void => {
    setBusy(true)
    setErr('')
    runSync()
      .then((run) => {
        status.refresh()
        if (!run.ok) setShown(run)
      })
      .catch((e: Error) => setErr(e.message))
      .finally(() => setBusy(false))
  }

  const last = status.data?.runs[0]
  const button = (
    <Button
      size='small'
      variant='outlined'
      startIcon={busy ? <CircularProgress size={14} /> : <Sync />}
      disabled={busy || !status.data?.configured}
      onClick={sync}
    >
      {busy ? 'Syncing…' : 'Sync now'}
    </Button>
  )

  if (props.compact) {
    return (
      <Stack
        direction='row'
        spacing={1}
        alignItems='center'
        sx={{ flexWrap: 'wrap' }}
      >
        <Typography sx={{ fontSize: 13, color: c.muted }}>
          Microsoft Entra
        </Typography>
        <Box
          sx={{ cursor: last ? 'pointer' : undefined }}
          onClick={() => setShown(last)}
        >
          <RunChip run={last} />
        </Box>
        {button}
        {err && (
          <Typography sx={{ fontSize: 12, color: 'error.main' }}>
            {err}
          </Typography>
        )}
        <LogDialog run={shown} onClose={() => setShown(undefined)} />
      </Stack>
    )
  }

  return (
    <Card
      elevation={0}
      sx={{ borderRadius: '16px', border: `1px solid ${c.line}`, p: 2.5 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography
          component='h2'
          className='erecords-serif'
          sx={{ fontFamily: serif, fontSize: 22, flex: 1 }}
        >
          Sync status
        </Typography>
        {button}
      </Box>
      <Typography sx={{ fontSize: 14, color: c.muted, mb: 2 }}>
        Creates GoAlert users for the “eRecords Admins” group before their first
        sign-in, merges the duplicate the first Microsoft sign-in creates, adds
        their Entra phone as Voice + SMS contact methods, copies their Microsoft
        profile photo, and keeps the admin role in step with the group. Runs
        every {status.data?.interval ?? '1h'}.
      </Typography>
      {status.data && !status.data.configured && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          Not configured: set ERECORDS_ENTRA_TENANT_ID,
          ERECORDS_ENTRA_CLIENT_ID, ERECORDS_ENTRA_CLIENT_SECRET and
          ERECORDS_ENTRA_ADMINS_GROUP_ID on the GoAlert deployment.
        </Alert>
      )}
      {(err || status.error) && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {err || status.error?.message}
        </Alert>
      )}
      <Stack spacing={1}>
        {status.data?.runs.map((r) => (
          <Box
            key={r.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <RunChip run={r} />
            <Typography sx={{ fontSize: 13, color: c.muted, flex: 1 }}>
              #{r.id} · {r.trigger} · {new Date(r.startedAt).toLocaleString()}
            </Typography>
            <Button size='small' onClick={() => setShown(r)} disabled={!r.log}>
              Log
            </Button>
          </Box>
        ))}
      </Stack>
      <LogDialog run={shown} onClose={() => setShown(undefined)} />
    </Card>
  )
}

// EntraSyncPage is Admin > Microsoft Entra sync.
export function EntraSyncPage(): React.JSX.Element {
  return (
    <Page title='Microsoft Entra sync' maxWidth={1000}>
      <EntraSyncPanel />
    </Page>
  )
}
